/**
 * SendViaGmailDialog.js
 *
 * Opens from the "Export" dropdown on /applications/:id. Pre-fills the
 * recipient via the NDOR-backed `recruiter-email-suggestion` endpoint
 * and lets the user edit subject / body / format before asking the
 * backend to stage the pack as a Gmail draft.
 *
 * The user still clicks Send inside Gmail themselves — we never send.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Mail, Sparkles } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { toast } from 'sonner';
import { handleCreditsError } from '@/lib/handleCreditsError';
import { useCredits } from '@/lib/creditsContext';

const SOURCE_LABELS = {
  explicit_apply_to:    'from "apply to" mention',
  signature_block:      'from signature block',
  inferred_from_domain: 'inferred from domain',
  generic_mailbox:      'generic careers mailbox',
};

export default function SendViaGmailDialog({
  open, onOpenChange, pack, onDraftCreated,
}) {
  const defaultSubject =
    `Application — ${pack?.job?.title || 'Role'} at ${pack?.job?.company || 'Company'}`;
  const defaultBody =
    `Hi,\n\nPlease find attached my application for the ${pack?.job?.title || 'role'}`
    + `${pack?.job?.company ? ` at ${pack.job.company}` : ''}.\n\n`
    + `I'd welcome the chance to discuss how my experience maps to what `
    + `you're looking for.\n\nBest regards,`;

  const [recipient, setRecipient] = useState('');
  const [source, setSource] = useState(null);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [suggesting, setSuggesting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { refresh: refreshCredits } = useCredits();

  // Only fetch suggestion when the dialog opens.
  const loadSuggestion = useCallback(async () => {
    if (!pack?.id) return;
    setSuggesting(true);
    try {
      const { suggestions = [] } = await apiFetch(
        `/api/application-packs/${pack.id}/recruiter-email-suggestion`,
      );
      if (suggestions.length > 0) {
        setRecipient(suggestions[0].email);
        setSource(suggestions[0].source);
      } else {
        setSource(null);
      }
      refreshCredits();
    } catch (err) {
      // 402 → shared credits toast. Anything else is swallowed so
      // the dialog stays usable even without a suggestion.
      handleCreditsError(err, {
        refreshCredits, action: 'a recruiter-email suggestion',
      });
      setSource(null);
    } finally {
      setSuggesting(false);
    }
  }, [pack?.id, refreshCredits]);

  useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setBody(defaultBody);
      setRecipient('');
      setSource(null);
      loadSuggestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pack?.id]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await apiFetch(
        `/api/application-packs/${pack.id}/gmail/create-draft`,
        {
          method: 'POST',
          body: {
            recipient_email: recipient,
            subject,
            body,
          },
        },
      );
      toast.success('Draft staged in Gmail.');
      if (onDraftCreated) onDraftCreated(res);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.message || 'Could not create Gmail draft.');
    } finally {
      setSubmitting(false);
    }
  };

  const disableSubmit =
    submitting || !recipient || !subject || !pack?.id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="send-gmail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" /> Send via Gmail
          </DialogTitle>
          <DialogDescription>
            Stage a Gmail draft with the exported bundle attached.
            Review and send inside Gmail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">
              Recipient
              {suggesting && (
                <span className="ml-2 inline-flex items-center text-xs text-ndor-ink-muted">
                  <Loader2 className="h-3 w-3 animate-spin mr-1" /> finding recruiter email…
                </span>
              )}
              {!suggesting && source && (
                <span className="ml-2 inline-flex items-center text-xs text-ndor-ink-muted">
                  <Sparkles className="h-3 w-3 mr-1" /> {SOURCE_LABELS[source] || source}
                </span>
              )}
            </Label>
            <Input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="recruiter@example.com"
              data-testid="send-gmail-recipient-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="send-gmail-subject-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Body</Label>
            <Textarea
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="send-gmail-body-input"
            />
          </div>

          {/* RC 1.51 follow-up — Export Simplification (operator-
              locked, 2026-02). Attachment format selector retired —
              Gmail drafts now always attach the single fixed-format
              Application Pack ZIP (Tailored CV + Cover Letter as
              DOCX, governance artefacts as PDF). */}
        </div>

        <DialogFooter>
          <Button
            variant="ghost" onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="send-gmail-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit} disabled={disableSubmit}
            data-testid="send-gmail-submit"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            Create draft in Gmail
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
